import { useSendTransaction, useWallets } from '@privy-io/react-auth';
import { useMutation } from '@tanstack/react-query';
import { usePrimaryChainVertexClient } from '@vertex-protocol/react-client';
import { useSubaccountContext } from 'client/context/subaccount/SubaccountContext';
import { useRefetchQueriesOnContractTransaction } from 'client/hooks/execute/util/useRefetchQueries';
import { tokenAllowanceQueryKey } from 'client/hooks/query/useTokenAllowance';
import { EXECUTE_DEPOSIT_COLLATERAL_REFETCH_QUERY_KEYS } from 'client/hooks/execute/useExecuteDepositCollateral';
import { useMemo } from 'react';
import { Address, encodeFunctionData, parseAbi, stringToHex } from 'viem';
import { INK_DEPOSIT_CHAIN } from '../config/chains';
import { ERC20_ABI } from '../config/oft';

const ENDPOINT_DEPOSIT_ABI = parseAbi([
  'function depositCollateral(bytes12,uint32,uint128)',
]);

interface SponsoredNadoApproveParams {
  productId: number;
  amount: bigint;
}

interface SponsoredNadoDepositParams {
  productId: number;
  amount: bigint;
}

interface UseSponsoredNadoTransactionsParams {
  connectedAddress: `0x${string}` | undefined;
  tokenAddress: Address | undefined;
}

export function useSponsoredNadoTransactions({
  connectedAddress,
  tokenAddress,
}: UseSponsoredNadoTransactionsParams) {
  const { sendTransaction } = useSendTransaction();
  const { wallets } = useWallets();
  const vertexClient = usePrimaryChainVertexClient();
  const {
    currentSubaccount: { name: subaccountName },
  } = useSubaccountContext();

  const endpointAddress = vertexClient?.context.contractAddresses
    .endpoint as Address | undefined;

  const embeddedWalletAddress = useMemo(() => {
    if (!connectedAddress) {
      return undefined;
    }

    return wallets.find(
      (wallet) =>
        wallet.walletClientType === 'privy' &&
        wallet.address.toLowerCase() === connectedAddress.toLowerCase(),
    )?.address as Address | undefined;
  }, [connectedAddress, wallets]);

  const canSponsor = !!embeddedWalletAddress && !!endpointAddress;

  const approve = useMutation({
    mutationFn: async ({
      amount,
    }: SponsoredNadoApproveParams): Promise<`0x${string}`> => {
      if (!canSponsor || !embeddedWalletAddress || !endpointAddress) {
        throw new Error('Privy sponsored approval is not available');
      }
      if (!tokenAddress) {
        throw new Error('Nado token address is not available');
      }

      const result = await sendTransaction(
        {
          to: tokenAddress,
          data: encodeFunctionData({
            abi: ERC20_ABI,
            functionName: 'approve',
            args: [endpointAddress, amount],
          }),
          chainId: INK_DEPOSIT_CHAIN.chainId,
        },
        {
          address: embeddedWalletAddress,
          sponsor: true,
          uiOptions: {
            description: 'Approve token for Nado deposit',
            buttonText: 'Approve',
          },
        },
      );

      return result.hash;
    },
  });

  const deposit = useMutation({
    mutationFn: async ({
      amount,
      productId,
    }: SponsoredNadoDepositParams): Promise<`0x${string}`> => {
      if (!canSponsor || !embeddedWalletAddress || !endpointAddress) {
        throw new Error('Privy sponsored deposit is not available');
      }

      const result = await sendTransaction(
        {
          to: endpointAddress,
          data: encodeFunctionData({
            abi: ENDPOINT_DEPOSIT_ABI,
            functionName: 'depositCollateral',
            args: [
              stringToHex(subaccountName, { size: 12 }),
              productId,
              amount,
            ],
          }),
          chainId: INK_DEPOSIT_CHAIN.chainId,
        },
        {
          address: embeddedWalletAddress,
          sponsor: true,
          uiOptions: {
            description: 'Deposit collateral into Nado',
            buttonText: 'Deposit',
          },
        },
      );

      return result.hash;
    },
  });

  useRefetchQueriesOnContractTransaction(
    [tokenAllowanceQueryKey()],
    approve.data,
  );
  useRefetchQueriesOnContractTransaction(
    EXECUTE_DEPOSIT_COLLATERAL_REFETCH_QUERY_KEYS,
    deposit.data,
  );

  return {
    canSponsor,
    approve,
    deposit,
  };
}
